import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { SocketProvider } from './context/SocketContext';
import { NotifyProvider } from './context/NotifyContext';
import SupportButton from './components/SupportButton';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import CreateWebsitePage from './pages/CreateWebsitePage';
import EditWebsitePage from './pages/EditWebsitePage';
import LegalPage from './pages/LegalPage';
import ContactPage from './pages/ContactPage';
import OfferingPage from './pages/OfferingPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import BillingPage from './pages/BillingPage';
import InvoiceDetailPage from './pages/InvoiceDetailPage';
import ProfilePage from './pages/ProfilePage';
import UnsubscribePage from './pages/UnsubscribePage';

// Create a query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="nova-page flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-nova-sky"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/terms-and-conditions" element={<LegalPage doc="terms" />} />
      <Route path="/terms" element={<Navigate to="/terms-and-conditions" replace />} />
      <Route path="/privacy-policy" element={<LegalPage doc="privacy" />} />
      <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
      <Route path="/cookie-policy" element={<LegalPage doc="cookies" />} />
      <Route path="/cookies" element={<Navigate to="/cookie-policy" replace />} />
      <Route path="/refund-and-cancellation-policy" element={<LegalPage doc="refunds" />} />
      <Route path="/refund-policy" element={<Navigate to="/refund-and-cancellation-policy" replace />} />
      <Route path="/refunds" element={<Navigate to="/refund-and-cancellation-policy" replace />} />
      <Route path="/contact-us" element={<ContactPage />} />
      <Route path="/contact" element={<Navigate to="/contact-us" replace />} />
      <Route path="/what-you-get" element={<OfferingPage />} />
      <Route path="/inclusions" element={<Navigate to="/what-you-get" replace />} />
      <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
      <Route
        path="/create-website"
        element={
          <ProtectedRoute>
            <CreateWebsitePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/website/:websiteId/published"
        element={
          <ProtectedRoute>
            <PaymentSuccessPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/website/:websiteId"
        element={
          <ProtectedRoute>
            <EditWebsitePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <BillingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <ProtectedRoute>
            <BillingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices/:invoiceId"
        element={
          <ProtectedRoute>
            <InvoiceDetailPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <SocketProvider>
            <Router>
              <NotifyProvider>
                <AppRoutes />
                {/* Inside Router so it can read the current path for support context. */}
                <SupportButton />
              </NotifyProvider>
            </Router>
          </SocketProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
